import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon as CaretDownIconSmall } from '@persona-id/design-tokens/icons';
import { Placement } from '@popperjs/core';
import Fuse from 'fuse.js';
import { ChangeEvent, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Tooltip from 'dashboard/components/Tooltip';
import TruncatedText from 'dashboard/components/TruncatedText';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import { Paragraph } from 'design-system/components/Text';
import inputMixin, { InputMixinProps, InputSizes } from 'design-system/mixins/input';
import useDebounced from 'lib/hooks/useDebounced';
import useToggle from 'lib/hooks/useToggle';

const OptionLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  display: -webkit-box;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const OptionSubtext = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  color: ${Color.CoreSlate500};
`;

export const optionWithSubtextMapper = (label: string, subtext: string): React.ReactNode => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <OptionLabel>{label}</OptionLabel>
      <OptionSubtext>{subtext}</OptionSubtext>
    </div>
  );
};

const Container = styled.div`
  position: relative;
`;

export const MultiSelectPlaceholder = styled.div<InputMixinProps & PlaceholderProps>`
  ${inputMixin};

  min-width: ${({ minWidth = '200px' }) => minWidth};

  /* Fixes weird issue with shadow appearing inside placeholder text */
  user-select: none;

  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledIconCaretDownSmall = styled(CaretDownIconSmall)`
  pointer-events: none;
`;

const OptionsDropdown = styled.datalist`
  /* there's some user agent stylesheet that sets datalist to display: none; */
  display: block;
  max-height: 350px;
  overflow-y: scroll;
  min-width: 0;
  padding: ${Spacing[4]};
  z-index: ${Z_INDICES.modal};
  text-decoration: none;
  outline: 0 !important;
  &:not(:first-child) {
    padding-top: 0px;
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0px;
`;

const StyledTooltip = styled(Tooltip)`
  padding: 0;
  margin-top: ${Spacing[2]};
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
  padding: ${Spacing[1]};
  width: 10%;
  svg {
    width: 24px;
    height: 100%;
    circle {
      stroke: ${Color.BorderIconSecondary};
    }
  }
`;

export interface PlaceholderProps {
  minWidth?: string;
}

export interface SelectOptionValue<SelectOptionValueType> {
  label: ReactNode;
  // supports custom display when search is enabled, as Fuse
  // expects selectOptionValue attributes to be strings or objects
  customLabel?: ReactNode;
  value: SelectOptionValueType;
}

export interface MultiSelectProps<SelectOptionValueType> {
  selectOptionValues: SelectOptionValue<SelectOptionValueType>[];
  labelTextMapper: (values: SelectOptionValueType[]) => ReactNode;
  onChange: (values: SelectOptionValueType[]) => void;
  onSearchChange?: (searchTerm: string) => void;
  value: SelectOptionValueType[] | '';
  onOpenOptions?: () => void;
  onCloseOptions?: () => void;
  onMouseOverOption?: (value: SelectOptionValueType) => void;
  onLoadMore?: () => void;
  disabled?: boolean;
  'data-test'?: string;
  className?: string;
  selectAllEnabled?: boolean;
  selectAllLabel?: string;
  searchEnabled?: boolean;
  optionSearchKey?: 'label' | 'value'; // These map to keys on SelectOptionValue
  placement?: Placement;
  minWidth?: string;
  hasError?: boolean;
  size?: InputSizes;
  loading?: boolean;
  /**
   * Generally speaking, sort should be false if the option values are a
   * constant. We assume the constant values are defined in the order to be
   * presented.
   */
  sort?: boolean;
}

const MultiSelect = <SelectOptionValueType,>({
  selectOptionValues: unsortedOptionValues,
  labelTextMapper,
  onChange,
  onSearchChange,
  onCloseOptions,
  onOpenOptions,
  onMouseOverOption,
  onLoadMore,
  value: fieldValue = [],
  disabled = false,
  'data-test': dataTest,
  className,
  selectAllEnabled = false,
  selectAllLabel = 'Select All',
  searchEnabled = false,
  optionSearchKey = 'label',
  placement = 'bottom',
  minWidth = '200px',
  hasError = false,
  size = 'md',
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  loading = null,
  sort = false,
}: MultiSelectProps<SelectOptionValueType>) => {
  const sortedOptionValues = useMemo(
    () =>
      sort
        ? [...unsortedOptionValues].sort((a, b) => {
            const aLabel = a.customLabel ?? a.label;
            const bLabel = b.customLabel ?? b.label;

            if (typeof aLabel === 'string' && typeof bLabel === 'string') {
              return aLabel.localeCompare(bLabel);
            } else {
              // Cannot sort React nodes
              return 0;
            }
          })
        : unsortedOptionValues,
    [sort, unsortedOptionValues],
  );

  // Redux-form defaults value to ''. We force it to [] if no other value is supplied.
  // We also filter out any value that is not included in the list of options.
  const selectedOptions = useMemo(
    () =>
      fieldValue === ''
        ? []
        : fieldValue.filter((v) => sortedOptionValues.some((opt) => opt.value === v)),
    [fieldValue, sortedOptionValues],
  );

  const [showOptionsDropdown, { toggle: toggleShowOptionsDropdown }] = useToggle(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleSetSearchTerm = (value: string) => {
    setSearchTerm(value);
    onSearchChange?.(value);
  };

  const [debouncedSetSearchTerm] = useDebounced(handleSetSearchTerm, 100, {
    leading: false,
    trailing: true,
  });

  const searchResults = useMemo(() => {
    if (!searchEnabled) {
      return sortedOptionValues;
    }

    const fuseOptions = {
      shouldSort: true,
      theshold: 0.0,
      ignoreLocation: true,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: optionSearchKey, weight: 1.0 }],
    };

    const fuse = new Fuse<SelectOptionValue<SelectOptionValueType>>(
      sortedOptionValues,
      fuseOptions,
    );

    return searchTerm !== ''
      ? Array.from(new Set(fuse.search(searchTerm).map(({ item }) => item)).values())
      : sortedOptionValues;
  }, [searchEnabled, sortedOptionValues, optionSearchKey, searchTerm]);

  const handleOptionsDropdownClick = () => {
    if (showOptionsDropdown && onCloseOptions) {
      onCloseOptions();
    }
    if (!disabled) {
      setSearchTerm('');
      toggleShowOptionsDropdown();
      if (!showOptionsDropdown) {
        if (onOpenOptions != null) {
          onOpenOptions();
        }
      }
    }
  };

  const onOptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      onChange(
        e.target.checked
          ? selectedOptions.concat([e.target.value as unknown as SelectOptionValueType])
          : selectedOptions.filter(
              (v) => v !== (e.target.value as unknown as SelectOptionValueType),
            ),
      ),
    [onChange, selectedOptions],
  );

  const onSelectAllChange = useCallback(() => {
    const notAllSelected = selectedOptions.length < sortedOptionValues.length;
    onChange(
      notAllSelected
        ? sortedOptionValues.map((option: SelectOptionValue<SelectOptionValueType>) => option.value)
        : [],
    );
  }, [onChange, selectedOptions, sortedOptionValues]);

  const displayedPlaceholder = labelTextMapper(selectedOptions);
  const options = searchResults.map(({ customLabel, label, value }) => {
    const checked = selectedOptions.includes(value);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const checkboxId = `multi-select__option__checkbox--${value.toString()}`;
    return (
      <Option
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        key={value.toString()}
        role='option'
        aria-selected={checked}
        onMouseOver={() => {
          onMouseOverOption && onMouseOverOption(value);
        }}
      >
        <Checkbox
          data-test='multi-select__option'
          id={checkboxId}
          value={value}
          label={customLabel ?? label}
          checked={checked}
          onChange={onOptionChange}
        />
      </Option>
    );
  });

  if (selectAllEnabled) {
    const value = 'selectAll';
    const checked = selectedOptions.length === sortedOptionValues.length;
    const indeterminate =
      selectedOptions.length && selectedOptions.length < sortedOptionValues.length;
    const checkboxId = value.toString();
    const selectAllOptionComponent = (
      <Option
        key={value.toString()}
        role='option'
        aria-selected={checked}
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-labelledby={checkboxId}
      >
        <Checkbox
          data-test='multi-select__select_all_option'
          id={checkboxId}
          value={value}
          onChange={onSelectAllChange}
          checked={checked}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          indeterminate={indeterminate}
          label={selectAllLabel}
          aria-label={!selectAllLabel && 'Select all'}
          disabled={disabled}
        />
      </Option>
    );
    options.unshift(selectAllOptionComponent);
  }

  if (onLoadMore) {
    options.push(
      <Button
        key='load-button'
        data-test='multi-select__load-more'
        id='multi-select__load-more'
        variant='basic'
        onClick={onLoadMore}
      >
        Load more
      </Button>,
    );
  }

  const containerRef = useRef(null);
  const isLoading = loading != null && loading;

  return (
    <Container data-test={dataTest} className={className} ref={containerRef}>
      <StyledTooltip
        hideArrow
        trigger='click'
        placement={placement}
        disabled={disabled}
        onVisibilityChange={handleOptionsDropdownClick}
        tooltipShown={showOptionsDropdown}
        enablePopoverClickEvents
        tooltip={
          <Container
            style={{
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              width: `${containerRef.current ? containerRef.current.scrollWidth + 'px' : 'auto'}`,
              pointerEvents: 'auto',
            }}
          >
            {searchEnabled && (
              <SearchInput
                title={null}
                value={searchTerm}
                handleInput={debouncedSetSearchTerm}
                placeholder='Search...'
                data-test='multi-select__search'
                useClearIcon
                autoFocus
                flattenDisplay
              />
            )}
            <OptionsDropdown aria-multiselectable>{options}</OptionsDropdown>
          </Container>
        }
      >
        <MultiSelectPlaceholder
          id={`${dataTest || 'multi-select'}__placeholder`}
          data-test={`${dataTest || 'multi-select'}__placeholder`}
          disabled={disabled}
          role='combobox'
          aria-expanded={showOptionsDropdown}
          aria-disabled={disabled}
          active={showOptionsDropdown}
          tabIndex={0}
          minWidth={minWidth}
          hasError={hasError}
          aria-invalid={hasError}
          $size={size}
        >
          <TruncatedText>{displayedPlaceholder}</TruncatedText>
          {isLoading ? <StyledLoadingIndicator /> : <StyledIconCaretDownSmall />}
        </MultiSelectPlaceholder>
      </StyledTooltip>
    </Container>
  );
};

export default MultiSelect;

interface UseLabelTextMapperOptions {
  emptyIsAll?: boolean;
  allSelectedLabel?: string;
}

/**
 * @param {SelectOptionValue[]} options
 * @param {string} unitLabel - pluralized form of the unit(s) being selected
 * @param {UseLabelTextMapperOptions} opts - configuration for the hook
 *
 */
export const useLabelTextMapper = (
  options: SelectOptionValue<string>[],
  unitLabel: string,
  opts: UseLabelTextMapperOptions = {},
) =>
  useCallback(
    (values: string[]) => {
      if (values.length === options.length) {
        return opts.allSelectedLabel ?? `All ${unitLabel}`;
      } else if (values.length === 1) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return options.find((v) => v.value === values[0]).label;
      } else if (values.length > 1) {
        return `${values.length} ${unitLabel}`;
      } else {
        return opts.emptyIsAll ? (opts.allSelectedLabel ?? `All ${unitLabel}`) : `No ${unitLabel}`;
      }
    },
    [options, unitLabel, opts],
  );
